import {
	createContext,
	useCallback,
	useContext,
	useState,
	PropsWithChildren,
	useRef,
	useEffect,
} from 'react'
import type { CareerListing, ApplicationFormFieldsState } from './types'
import {
	isValidEmail,
	formatApplicationFormData,
	COMPLIANCE_FIELD_NAME,
	MULTI_SELECT_DEFAULT,
	compareMonths,
} from './greenhouse-careers'
import { useForm, FormProvider } from 'react-hook-form'

type SubmissionStatus = 'idle' | 'pending' | 'success' | 'error'

export type FormState = {
	isVisible: boolean
	isValid: boolean
	submissionStatus: SubmissionStatus
}

interface ApplicationFormContextValue {
	formState: FormState
	showApplication: () => void
	hideApplication: () => void
	submitForm: (e: React.FormEvent<HTMLFormElement>) => void
}

export const ApplicationFormContext =
	createContext<ApplicationFormContextValue>({
		formState: {
			isVisible: false,
			isValid: false,
			submissionStatus: 'idle',
		},
		showApplication: () => {
			throw new Error('showApplication not implemented')
		},
		hideApplication: () => {
			throw new Error('hideApplication not implemented')
		},

		submitForm: () => {
			throw new Error('submitForm not implemented')
		},
	})

export interface ApplicationFormProviderProps extends PropsWithChildren {
	career: CareerListing
	initialFormState?: Partial<FormState>
	onSubmitClick?: (formData: FormData, listingId: string) => Promise<void>
	onSubmitSuccess?: () => void
	onSubmitError?: (err: $TSFixMe) => void
}

const DOMAIN = 'careers_application_form'
const EVENT = {
	SHOW: `${DOMAIN}__show`,
	HIDE: `${DOMAIN}__hide`,
	USER_INPUT_ERRORS: `${DOMAIN}__user_input_errors`,
	SUBMIT: `${DOMAIN}__submit`,
	SUBMIT_ERROR: `${DOMAIN}__submit_error`,
	SUBMIT_SUCCESS: `${DOMAIN}__submit_success`,
} as const

export const ApplicationFormProvider: React.FC<
	ApplicationFormProviderProps
> = ({
	children,
	career,
	onSubmitSuccess,
	onSubmitClick,
	onSubmitError,
	initialFormState = {},
}) => {
	const postSubmissionTimeout = useRef<NodeJS.Timeout | null>(null)

	// just to clean up the timeout when the component unmounts, we set this in the submit function
	useEffect(() => {
		return () => {
			if (postSubmissionTimeout.current) {
				clearTimeout(postSubmissionTimeout.current)
			}
		}
	}, [])

	const [formState, setFormState] = useState<FormState>({
		submissionStatus: 'idle',
		isVisible: false,
		isValid: false,
		...initialFormState,
	})

	const showApplication = useCallback(() => {
		window.scrollTo(0, 0)
		setFormState({
			isVisible: true,
			isValid: false,
			submissionStatus: 'idle',
		})
	}, [])

	const hideApplication = useCallback(() => {
		setFormState({
			isVisible: false,
			isValid: false,
			submissionStatus: 'idle',
		})
	}, [])

	const resolveFormErrors = (formValuesState: ApplicationFormFieldsState) => {
		const errorMap: Record<string, { message: string }> = {}
		const REQUIRED_MESSAGE = 'This field is required.'

		// 1. validate base questions
		for (const q of career.questions) {
			const formStateKey = q.fields[0].name
			const questionValue = formValuesState[formStateKey]

			const isEmptyString = !questionValue
			if (isEmptyString && q.required) {
				errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
			}

			const isEmail = q.fields[0].name === 'email'
			if (isEmail) {
				const isInvalidEmail = !isValidEmail(questionValue as string)
				if (isInvalidEmail) {
					errorMap[formStateKey] = { message: 'Please enter a valid email.' }
				}
			}

			const isFile = questionValue instanceof FileList
			const isEmptyFile = isFile && questionValue.length === 0
			if (isEmptyFile && q.required) {
				errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
			}

			if (isFile && !isEmptyFile) {
				// https://support.greenhouse.io/hc/en-us/articles/200989175-Unsuccessful-resume-parse#:~:text=Resume%20formatting%20issues-,The%20resume%20file%20is%20too%20large,be%20over%20this%20file%20limit.
				const TWO_AND_A_HALF_MB = 2.5 * 1024 * 1024
				const isTooLarge = questionValue[0].size >= TWO_AND_A_HALF_MB
				if (isTooLarge) {
					errorMap[formStateKey] = {
						message: 'File size must be less than 2.5MB.',
					}
				}
			}

			const isSentinelValue = questionValue === MULTI_SELECT_DEFAULT
			if (isSentinelValue) {
				errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
			}
		}

		// 2. validate education questions
		for (const q of career.hashi_edu_questions) {
			const formStateKey = q.id
			const questionValue = formValuesState[formStateKey]

			if (q.required) {
				const isEmptyString = !questionValue
				if (isEmptyString) {
					errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
				}
			}

			if (q.id === 'end_date') {
				const result = compareMonths(
					questionValue,
					formValuesState['start_date'],
					'>'
				)
				if (result.error) {
					errorMap[formStateKey] = { message: result.error }
				}
				if (!result.value) {
					errorMap[formStateKey] = {
						message: 'End date must be after start date.',
					}
				}
			}
		}

		// 3. validate demographic questions
		if (career.demographic_questions?.questions) {
			for (const q of career.demographic_questions.questions) {
				const formStateKey = q.id
				const questionValue = formValuesState[formStateKey]

				if (q.required) {
					const isEmptyString = !questionValue
					if (isEmptyString) {
						errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
					}

					const isSentinelValue = questionValue === MULTI_SELECT_DEFAULT
					if (isSentinelValue) {
						errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
					}

					// validates that the answer is in the list of options
					// unlikely to happen, but just in case
					const isInvalidAnswer = !q.answer_options.some(
						(o) => String(o.id) === questionValue
					)
					if (isInvalidAnswer) {
						errorMap[formStateKey] = { message: `Invalid answer.` }
					}
				}
			}
		}

		// 4. validate location question
		if (career.location_questions) {
			for (const q of career.location_questions) {
				const formStateKey = q.fields[0].name
				const questionValue = formValuesState[formStateKey]

				// Only validating the location field value
				if (q.required && formStateKey === 'location') {
					if (!questionValue) {
						errorMap[formStateKey] = { message: REQUIRED_MESSAGE }
					}
				}
			}
		}

		// lastly, check that the data compliance field is checked
		const hasCompliance = COMPLIANCE_FIELD_NAME in formValuesState
		const isComplianceRequired = career.data_compliance[0].requires_consent
		if (hasCompliance && isComplianceRequired) {
			const isComplianceChecked = formValuesState[COMPLIANCE_FIELD_NAME]
			if (!isComplianceChecked) {
				errorMap[COMPLIANCE_FIELD_NAME] = { message: REQUIRED_MESSAGE }
			}
		}

		const hasErrors = Object.keys(errorMap).length > 0
		setFormState({
			...formState,
			isValid: !hasErrors,
		})

		return errorMap
	}

	const methods = useForm<ApplicationFormFieldsState>({
		mode: 'all',
		resolver: (formValues) => {
			const errors = resolveFormErrors(formValues)
			return { values: formValues, errors }
		},
	})

	const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
		methods.handleSubmit(async (values) => {
			setFormState({
				...formState,
				submissionStatus: 'pending',
			})
			try {
				const formattedValues = await formatApplicationFormData(values)
				await onSubmitClick?.(formattedValues, String(career.id))
				onSubmitSuccess?.()
				methods.reset()
				setFormState({
					...formState,
					submissionStatus: 'success',
				})
				const successWaitPeriod = 1000
				postSubmissionTimeout.current = setTimeout(() => {
					setFormState({
						...formState,
						submissionStatus: 'idle',
					})
				}, successWaitPeriod)
			} catch (error) {
				onSubmitError?.(error)
				setFormState({
					...formState,
					submissionStatus: 'error',
				})
			}
		})(e)
	}

	return (
		<FormProvider {...methods}>
			<ApplicationFormContext.Provider
				value={{
					formState,
					showApplication,
					hideApplication,
					submitForm,
				}}
			>
				{children}
			</ApplicationFormContext.Provider>
		</FormProvider>
	)
}

export const useApplicationForm = () => {
	const context = useContext(ApplicationFormContext)
	if (!context) {
		const errorMsg =
			'useApplicationForm must be used within a ApplicationFormProvider'
		throw new Error(errorMsg)
	}
	return context
}
